import { PureComponent } from "react";

import autobind from "autobind-decorator";
import * as _ from "lodash";
import noop from "lodash/noop";
import { connect } from "react-redux";

import { toggleSideNav } from "app/actions/layoutActions";
import { swSettings } from "app/common/services/swSettings";
import DashboardService from "app/components/dashboard/services/DashboardService";
import type { IDashboard } from "app/components/dashboard/services/IDashboard";
import type { INavItem, ISideNavProps, ISideNavState } from "app/components/React/SideNavComponents/SideNav.types";
import { isBeta, isLocked, isNew } from "app/components/React/SideNavComponents/SideNav.utils";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { RootState, ThunkDispatchCommon } from "single-spa/store/types";
import { SwTrack } from "app/services/SwTrack";

import { keywordGroupsQueryClient } from "app/services/keyword-groups";

import { setSharedWithMeDashboards } from "./DashboardSideNavActions";
import { ShareDashboardService } from "./ShareDashboardService";

type MenuItem = "rename" | "duplicate" | "share" | "erase";

type DashboardMenuItem = { id: MenuItem; disabled?: boolean };

// Dashboard sub-items are IDashboard records surfaced through the nav list (cast to INavItem by the
// reducer), so this file reads both INavItem fields and the dashboard-only sharing/link fields.
type DashboardNavItem = INavItem & Pick<IDashboard, "id" | "link" | "isSharedByMe" | "isSharedWithMe">;

interface IDashboardSideNavProps extends ISideNavProps {
    // eslint-disable-next-line react/no-unused-prop-types -- read via getCurrentActive(props), not this.props, so the rule can't trace usage
    currentStateId?: string;
}

interface IDashboardNavState extends ISideNavState {
    isButtonLoading?: boolean;
    editedItem: any;
    items: any;
    initialLoad: boolean;
    subItemNameList: string[];
}

class DashboardSideNav extends PureComponent<IDashboardSideNavProps, IDashboardNavState> {
    private services;
    private editableProperties;

    constructor(props: IDashboardSideNavProps) {
        super(props);

        this.services = {
            swNavigator: proNavigator,
        };

        const currentActiveItem = this.getCurrentActive(this.props);

        this.state = {
            activeItemName: currentActiveItem,
            editedItem: {},
            items: this.getSideNavList(currentActiveItem),
            isButtonLoading: false,
            initialLoad: true,
            subItemNameList: [],
        };

        this.editableProperties = {
            isDashboard: false,
            isEditable: true,
            isFocused: true,
            isSelected: true,
            isError: false,
            onItemChange: noop,
            onItemCancelEdit: this.onItemCancelEdit,
            onItemEdit: this.onItemEdit,
            debounce: 0,
        };
        this.props.setSideNavItems();
    }

    public componentDidUpdate(prevProps: IDashboardSideNavProps) {
        const active = this.getCurrentActive(this.props);
        const prevActive = this.getCurrentActive(prevProps);

        if (active !== prevActive || this.props.navList !== prevProps.navList || this.state.initialLoad) {
            this.setState({
                activeItemName: active,
                items: this.getSideNavList(active, this.props),
                initialLoad: false,
                subItemNameList: this.getAllSubItemNames(this.props.navList),
            });
        }
    }

    public render() {
        return null;
    }

    private getAllSubItemNames(itemsTree: INavItem[]) {
        return itemsTree.reduce((flat: string[], toFlatten: INavItem) => {
            return flat.concat(
                Array.isArray(toFlatten.subItems) ? toFlatten.subItems.map((sub: INavItem) => sub.title) : [],
            );
        }, []);
    }

    @autobind
    private getCurrentActive(props: IDashboardSideNavProps): string {
        // @ts-ignore FIXME: strict migration - return is 'string | undefined' but callers treat the active id as 'string' — preserves legacy contract without narrowing shared ISideNavState.activeItemName
        return props.currentStateId ? props.currentStateId : props.currentPage;
    }

    @autobind
    private onCreateNewButtonClick() {
        SwTrack.all.trackEvent("Dashboards", "click", "Side Bar/Add a New Dashboard");
        this.services.swNavigator.go("dashboard-gallery");
    }

    @autobind
    private getSideNavList(activeItemName: string, props?: IDashboardSideNavProps) {
        const _props = props || this.props;
        return _props.navList.map((listItem: INavItem) => {
            return {
                ...listItem,
                // @ts-ignore FIXME: strict migration - 'listItem.subItems' is possibly 'undefined' — INavItem.subItems optional, dashboard nav items always populate it
                subItems: listItem.subItems.map((subItem: INavItem) => {
                    return { ...this.interpretNavItem(subItem as DashboardNavItem, activeItemName) };
                }),
            };
        });
    }

    @autobind
    private interpretNavItem(navListItem: DashboardNavItem, activeItemName: string) {
        let menuItems: DashboardMenuItem[] = [{ id: "rename" }, { id: "duplicate" }, { id: "share" }, { id: "erase" }];
        // Duplicate dashboard to other users is available only for internal users.
        if (!swSettings.components.Home.resources.CanDuplicateDashboardToOthers) {
            menuItems = menuItems.filter((item) => item.id !== "share");
        }

        if (!swSettings.components.Dashboard.resources.CanDuplicateADashboard) {
            menuItems = menuItems.map((item: DashboardMenuItem) => {
                if (item.id === "duplicate") {
                    item.disabled = true;
                }
                return item;
            });
        }

        if (navListItem.isSharedWithMe) {
            menuItems = menuItems.map((item: DashboardMenuItem) => {
                if (item.id !== "duplicate") {
                    item.disabled = true;
                }
                return item;
            });
        }

        const result = {
            isSharedByMe: navListItem.isSharedByMe,
            isSharedWithMe: navListItem.isSharedWithMe,
            title: navListItem.title,
            name: navListItem.name,
            isDisabled: false,
            isBeta: isBeta(navListItem),
            isNew: isNew(navListItem),
            isLocked: isLocked(navListItem),
            isActive: activeItemName === navListItem.id,
            url: navListItem.link,
            // specific for dashboard
            isDashboard: true,
            isLoading: false,
            menuItems,
            id: navListItem.id,
            deletePopUpIsOpen: navListItem.deletePopUpIsOpen,
            onCancel: () => this.onCancelDeleteClick(navListItem),
            onConfirm: () => this.onConfirmDeleteClick(navListItem),
            onMenuToggle: () => noop,
            onMenuClick: (selectedMenuItem: DashboardMenuItem) =>
                this.onNavItemMenuClick(selectedMenuItem, navListItem),
            onClick: () => this.onNavItemClick(navListItem),
        };

        return result;
    }

    @autobind
    private onNavItemClick(navListItem: DashboardNavItem) {
        SwTrack.all.trackEvent("Dashboards", "click", "Side Bar/" + navListItem.title);
        if (window.outerWidth <= 1200) {
            this.props.toggleSideNav();
        }
        this.setState({
            // @ts-ignore FIXME: strict migration - 'navListItem.state' is 'string | undefined' but state.activeItemName is 'string' — preserves legacy assignment without narrowing shared ISideNavState
            activeItemName: navListItem.state,
        });
    }

    @autobind
    private onNavItemMenuClick(item: DashboardMenuItem, navListItem: DashboardNavItem) {
        switch (item.id) {
            case "rename":
                SwTrack.all.trackEvent("Dashboards", "click", "Side Bar/Edit");
                this.setPropertiesToItem(
                    navListItem,
                    this.editableProperties,
                    this.setState({ editedItem: navListItem }),
                );
                break;
            case "erase":
                SwTrack.all.trackEvent("Dashboards", "click", "Side Bar/Delete");
                this.setPropertyToItem(navListItem, "deletePopUpIsOpen");
                break;
            case "duplicate":
                SwTrack.all.trackEvent("Duplicate Report", "click", "Side Bar");
                this.onDuplicateDashboard(navListItem);
                break;
            case "share":
                this.onShareDashboard(navListItem);
                break;
        }
    }

    @autobind
    private onItemCancelEdit() {
        this.setPropertiesToItem(this.state.editedItem, { isDashboard: true, isEditable: false });
    }

    @autobind
    private onItemEdit(newItemTitle: string) {
        if (this.state.editedItem.title === newItemTitle) {
            this.setPropertiesToItem(this.state.editedItem, {
                isDashboard: true,
                isEditable: false,
                isLoading: false,
            });
            return;
        }
        if (this.isItemValid(newItemTitle)) {
            SwTrack.all.trackEvent("Dashboards", "click", "Side Bar/Edit/" + newItemTitle);
            this.setPropertiesToItem(
                this.state.editedItem,
                { isLoading: true, isError: false },
                _.debounce(() => this.renameDashboard(this.state.editedItem, newItemTitle), 100),
            );
        } else {
            this.setPropertyToItem(this.state.editedItem, "isError");
        }
    }

    @autobind
    private isItemValid(newItemTitle: string) {
        return DashboardService.validateDashboardTitle(this.state.editedItem, newItemTitle);
    }

    @autobind
    private onCancelDeleteClick(_navListItem: DashboardNavItem) {
        SwTrack.all.trackEvent("Dashboards", "click", "Side Bar/Delete/No");
        this.setState({
            items: this.getSideNavList(this.state.activeItemName),
        });
    }

    @autobind
    private onConfirmDeleteClick(navListItem: DashboardNavItem) {
        SwTrack.all.trackEvent("Dashboards", "click", "Side Bar/Delete/Yes");
        this.setPropertyToItem(navListItem, "isLoading", () => {
            this.deleteDashboard(navListItem);
        });
    }

    @autobind
    private onShareDashboard(_navListItem: DashboardNavItem) {}

    @autobind
    private renameDashboard(navListItem: DashboardNavItem, newTitle: string) {
        const oldTitle = navListItem.title;
        const setPropertiesToItem = this.setPropertiesToItem;
        DashboardService.renameDashboard({ id: navListItem.id, title: newTitle }).then(
            () => {
                this.props.setSideNavItems();
                setPropertiesToItem(navListItem, {
                    title: newTitle,
                    isDashboard: true,
                    isEditable: false,
                    isLoading: false,
                });
            },
            function fail() {
                setPropertiesToItem(navListItem, {
                    title: oldTitle,
                    isDashboard: false,
                    isEditable: true,
                    isLoading: false,
                    isError: true,
                });
            },
        );
    }

    @autobind
    private async deleteDashboard(navListItem: DashboardNavItem) {
        const swNavigator = this.services.swNavigator;
        const fail = () => {
            this.setState({
                items: this.getSideNavList(this.state.activeItemName),
            });
        };
        const success = () => {
            this.props.setSideNavItems();
            if (DashboardService.dashboards.length) {
                const dashboard = DashboardService.getFirstDashboard();
                if (dashboard.name && dashboard.name === "newdashboard") {
                    swNavigator.go("dashboard-new"); // if the last item is the "add dashboard" button
                } else {
                    swNavigator.go("dashboard-exist", {
                        dashboardId: dashboard.id,
                    });
                }
            } else {
                swNavigator.go("dashboard-gallery");
            }
        };

        try {
            if (navListItem.isSharedByMe) {
                const { err, success } = await ShareDashboardService.unShare({
                    dashboardId: navListItem.id,
                });
                if (err || !success) {
                    fail();
                    return;
                }
            }
            await DashboardService.deleteDashboard({ id: navListItem.id });
            success();
        } catch {
            fail();
        }
    }

    @autobind
    private setPropertyToItem(navListItem: DashboardNavItem, property: string, callback?: FixmeType) {
        const items = this.state.items.map((listItem: FixmeType) => {
            return {
                ...listItem,
                subItems: listItem.subItems.map((subItem: FixmeType) => {
                    subItem[property] = navListItem.id === subItem.id;
                    return { ...subItem };
                }),
            };
        });

        this.setState(
            {
                items,
            },
            callback,
        );
    }

    @autobind
    private setPropertiesToItem(navListItem: DashboardNavItem, properties: FixmeType, callback?: FixmeType) {
        const items = this.state.items.map((listItem: FixmeType) => {
            return {
                ...listItem,
                subItems: listItem.subItems.map((subItem: FixmeType) => {
                    if (navListItem.id === subItem.id) {
                        subItem = Object.assign({}, subItem, properties);
                    }
                    return { ...subItem };
                }),
            };
        });

        this.setState(
            {
                items,
            },
            callback,
        );
    }

    @autobind
    private onDuplicateDashboard(navListItem: DashboardNavItem) {
        this.setPropertyToItem(navListItem, "isLoading", this.duplicateDashboard(navListItem));
    }

    @autobind
    private duplicateDashboard(navListItem: DashboardNavItem) {
        const swNavigator = this.services.swNavigator;
        DashboardService.cloneDashboard(navListItem).then(
            async (createdDashboard) => {
                await keywordGroupsQueryClient.invalidateAll();
                this.props.setSideNavItems();
                swNavigator.go("dashboard-created", {
                    // @ts-ignore FIXME: strict migration - 'createdDashboard' is possibly 'undefined' — cloneDashboard resolves a dashboard on success, undefined only on failure handled by the reject branch
                    dashboardId: createdDashboard.id,
                });
            },
            function fail(this: FixmeType) {
                this.setState({
                    items: this.getSideNavList(this.state.activeItemName),
                });
            },
        );
    }
}

function mapStateToProps(store: RootState) {
    const currentPage = store.routing.currentPage;
    const currentStateId = store.routing.currentStateId;
    const { navList } = store.customDashboard.dashboardSideNav;
    return {
        currentPage,
        currentStateId,
        navList,
    };
}

function mapDispatchToProps(dispatch: ThunkDispatchCommon) {
    return {
        toggleSideNav: () => {
            dispatch(toggleSideNav());
        },
        setSideNavItems: () => {
            dispatch(setSharedWithMeDashboards());
        },
    };
}

export const ConnectedDashboardSideNav = connect(mapStateToProps, mapDispatchToProps)(DashboardSideNav);
